/* ==========================================================================
   base.js — the whole theme runtime, loaded once with `defer`.

   CONVENTIONS (keep these when adding components)

   1. Every interactive element carries `data-action="..."`. Components listen
      once on their root and resolve the target with `e.target.closest('[data-action]')`
      so clicks on nested icons/spans still work.
   2. Async section updates go through `renderContent()`: fetch the page, parse
      it, and swap innerHTML for a fixed list of `[data-*]` anchors.
   3. Loading state is a `.loading` class on the component host, toggled by
      `toggleLoading()`.
   4. Section root elements carry `data-section="{{ section.id }}"`.
   5. Element ids are prefixed so JS can find them without knowing the section
      id: Slider-, Slide-, ProductForm-, ProductOptions-, ProductPrice-,
      ProductSubmit-, ProductSticky-, ProductGallery-, CollectionGrid-, Siblings-.

   SHARED ACTION NAMES

   Header ......... toggle-menu, close-menu, toggle-submenu, toggle-megamenu,
                    close-megamenu, toggle-search, close-search, reset-search
   Cart drawer .... close, increase-quantity, decrease-quantity, remove-item,
                    add-product, to-checkout
   Main product ... select-slide, open-lightbox, close-lightbox,
                    toggle-disclosure, add-to-cart, add-product, close-popup
   Collection ..... open-options, select-option, toggle-filter, clear-filters
   Anywhere ....... an element with the `cart-drawer-toggle` attribute opens
                    the cart drawer.
   ========================================================================== */

/* --- UTILITIES ------------------------------------------------------------ */

function debounce(fn, wait) {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(this, args), wait);
  };
}

function setCookie(name, value, days) {
  const expires = new Date(Date.now() + days * 24 * 60 * 60 * 1000).toUTCString();
  document.cookie = `${name}=${value}; expires=${expires}; path=/`;
}

function getCookie(name) {
  const match = document.cookie.match(new RegExp('(^| )' + name + '=([^;]+)'));
  return match ? match[2] : null;
}

/**
 * Formats cents with the shop money format. Only needed when a value is
 * computed client-side; anything rendered by Liquid should use the
 * `price-formated` snippet instead.
 */
function formatMoney(cents, format = window.themeSettings?.moneyFormat || '${{amount}}') {
  const value = typeof cents === 'string' ? cents.replace('.', '') : cents;

  const formatWithDelimiters = (number, precision = 2, thousands = ',', decimal = '.') => {
    if (isNaN(number) || number == null) return '0';

    const fixed = (number / 100.0).toFixed(precision);
    const parts = fixed.split('.');
    const dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
    const cts = parts[1] ? decimal + parts[1] : '';

    return dollars + cts;
  };

  const placeholder = /\{\{\s*(\w+)\s*\}\}/;
  const match = format.match(placeholder);
  if (!match) return format;

  let formatted;
  switch (match[1]) {
    case 'amount_no_decimals':
      formatted = formatWithDelimiters(value, 0);
      break;
    case 'amount_with_comma_separator':
      formatted = formatWithDelimiters(value, 2, '.', ',');
      break;
    case 'amount_no_decimals_with_comma_separator':
      formatted = formatWithDelimiters(value, 0, '.', ',');
      break;
    default:
      formatted = formatWithDelimiters(value, 2);
  }

  return format.replace(placeholder, formatted);
}

/**
 * Validates a single input and writes the message into `errorBlock`.
 * Messages come from window.errorStrings (see snippets/base.liquid).
 */
function reportInputValidity(input, errorBlock) {
  if (!input || !errorBlock) return false;

  const value = input.value.trim();
  let isValid = value !== '';
  let errorType = 'empty';

  if (isValid && input.type === 'email' && !input.checkValidity()) {
    isValid = false;
    errorType = 'email';
  }

  if (isValid) {
    input.removeAttribute('aria-invalid');
    errorBlock.innerText = '';
  } else {
    input.setAttribute('aria-invalid', true);
    errorBlock.innerText = window.errorStrings?.[errorType] ?? '';
  }

  return isValid;
}

/**
 * Reference-counted page scroll lock. Nested overlays (cart drawer opened on
 * top of a mobile menu) must not unlock the page when only one of them closes.
 *
 *   togglePageScroll(true)   -> lock
 *   togglePageScroll(false)  -> unlock
 *   togglePageScroll()       -> flip current state
 */
let pageScrollLocks = 0;

function togglePageScroll(force) {
  const shouldLock = force === undefined ? pageScrollLocks === 0 : force;

  if (shouldLock) {
    pageScrollLocks += 1;
  } else {
    pageScrollLocks = Math.max(0, pageScrollLocks - 1);
  }

  document.documentElement.style.overflow = pageScrollLocks > 0 ? 'hidden' : null;
}

/** Fetches a URL and returns a parsed Document. */
function fetchDocument(url) {
  return fetch(url)
    .then((response) => {
      if (!response.ok) throw new Error(`Request failed: ${url}`);
      return response.text();
    })
    .then((body) => new DOMParser().parseFromString(body, 'text/html'));
}

/**
 * Adds one variant to the cart and refreshes every [cart-count] in the theme.
 * <cart-drawer> owns a richer flow (it re-renders itself through the Section
 * Rendering API); this is the plain path for components that add to the cart
 * while no drawer is mounted.
 */
function addVariantToCart(variantId, quantity = 1) {
  return fetch(`${window.Shopify.routes.root}cart/add.js`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ items: [{ id: parseInt(variantId, 10), quantity }] })
  })
    .then((response) => response.json())
    .then((response) => {
      if (!response.items) throw new Error(response.description);
      return syncCartCount();
    })
    .catch((error) => console.error(error));
}

/** Writes the current cart item_count into every [cart-count] element. */
function syncCartCount() {
  return fetch(`${window.Shopify.routes.root}cart.js`)
    .then((response) => response.json())
    .then((cart) => {
      document.querySelectorAll('[cart-count]').forEach((el) => {
        el.textContent = cart.item_count;
      });

      return cart;
    });
}

/** Keeps the last N viewed product handles in localStorage. */
function setViewedProducts(handle, limit = 10) {
  if (!handle) return;

  try {
    const stored = getViewedProducts().filter((item) => item !== handle);
    stored.unshift(handle);
    localStorage.setItem('viewed-products', JSON.stringify(stored.slice(0, limit)));
  } catch (error) {
    console.warn('Unable to store viewed products', error);
  }
}

function getViewedProducts() {
  try {
    return JSON.parse(localStorage.getItem('viewed-products')) || [];
  } catch (error) {
    return [];
  }
}

/**
 * Loads the Swiper library once, on demand — reach for it only when a slider
 * needs loop/autoplay/nested behaviour that <slider-component> can't do.
 * The section stays responsible for swiper.css (it must be there on first
 * paint), this only pulls the script:
 *
 *   window.loadSwiper().then((Swiper) => new Swiper(el, options));
 */
window.loadSwiper = function loadSwiper() {
  if (window.swiperPromise) return window.swiperPromise;

  window.swiperPromise = new Promise((resolve, reject) => {
    if (window.Swiper) return resolve(window.Swiper);

    const source = window.themeAssets?.swiperJs;
    if (!source) return reject(new Error('window.themeAssets.swiperJs is not defined'));

    const script = document.createElement('script');
    script.src = source;
    script.onload = () => resolve(window.Swiper);
    script.onerror = () => reject(new Error('Unable to load Swiper'));
    document.head.appendChild(script);
  });

  return window.swiperPromise;
};

/**
 * Disclosure panels — accordions, filter groups, product details.
 *
 * CSS keeps the panel at `max-height: 0; overflow: hidden` with a transition;
 * this measures the content and writes the pixel height, so nothing depends on a
 * guessed maximum. Spacing must live on an inner element: padding on the panel
 * itself would be clipped and would break the measurement.
 *
 *   toggleDisclosure(panel, isOpen)
 *   toggleDisclosure(panel, true, false)   // no animation, for markup that renders open
 */
const openDisclosures = new Set();

function toggleDisclosure(panel, isOpen, animate = true) {
  if (!panel) return;

  if (!animate) panel.style.transition = 'none';

  if (isOpen) {
    panel.style.maxHeight = `${panel.scrollHeight}px`;
    openDisclosures.add(panel);
  } else {
    panel.style.maxHeight = '';
    openDisclosures.delete(panel);
  }

  if (!animate) {
    panel.offsetHeight; // flush the change before the transition comes back
    panel.style.transition = '';
  }
}

// Text rewraps on resize, so open panels are re-measured.
window.addEventListener('resize', () => {
  openDisclosures.forEach((panel) => {
    if (!panel.isConnected) {
      openDisclosures.delete(panel);
      return;
    }

    panel.style.maxHeight = `${panel.scrollHeight}px`;
  });
});

window.toggleDisclosure = toggleDisclosure;

/**
 * Sticky policy navigation: smooth-scrolls to a section and marks the one the
 * reader is on. Shared by the `main-policy` section and by the Shopify-rendered
 * /policies/* pages, which snippets/policy-page rebuilds into the same markup.
 *
 *   window.initPolicyNav(root)
 */
window.initPolicyNav = function initPolicyNav(root) {
  if (!root) return;

  const links = Array.from(root.querySelectorAll('[data-action="scroll-to-section"]'));
  if (links.length === 0) return;

  const targets = links.map((link) => root.querySelector(link.getAttribute('href')));

  const headerHeight = () => {
    return parseInt(getComputedStyle(document.documentElement).getPropertyValue('--header-height'), 10) || 0;
  };

  const setActive = (index) => {
    links.forEach((link, i) => link.classList.toggle('active', i === index));
  };

  const updateActive = () => {
    const line = window.scrollY + headerHeight() + 48;
    let active = 0;

    targets.forEach((target, index) => {
      if (target && target.getBoundingClientRect().top + window.scrollY <= line) active = index;
    });

    // The last section is rarely tall enough to reach the line — the page bottom marks it read.
    if (window.scrollY + window.innerHeight >= document.documentElement.scrollHeight - 2) {
      active = targets.length - 1;
    }

    setActive(active);
  };

  root.addEventListener('click', (event) => {
    const link = event.target.closest('[data-action="scroll-to-section"]');
    if (!link) return;

    const target = root.querySelector(link.getAttribute('href'));
    if (!target) return;

    event.preventDefault();
    setActive(links.indexOf(link));

    window.scrollTo({
      top: target.getBoundingClientRect().top + window.scrollY - headerHeight() - 24,
      behavior: 'smooth'
    });
  });

  let ticking = false;
  window.addEventListener('scroll', () => {
    if (ticking) return;
    ticking = true;

    requestAnimationFrame(() => {
      updateActive();
      ticking = false;
    });
  }, { passive: true });

  updateActive();
};

/**
 * Swiper 11 loops by reordering the real slides, so a track narrower than the
 * viewport leaves a gap instead of wrapping around. Pads the list with clones
 * until it is wide enough and reports whether looping is possible at all.
 */
function fillLoopTrack(element, spaceBetween) {
  const wrapper = element.querySelector('.swiper-wrapper');
  const originals = Array.from(wrapper?.children || []);
  if (originals.length < 2) return false;

  const step = originals[0].getBoundingClientRect().width + spaceBetween;
  if (!step) return false;

  const viewport = Math.max(window.innerWidth, window.screen?.width || 0);
  const needed = Math.ceil((viewport * 3) / step);

  while (wrapper.children.length < needed) {
    originals.forEach((slide) => {
      const clone = slide.cloneNode(true);
      clone.removeAttribute('data-shopify-editor-block');
      clone.querySelector('video')?.setAttribute('preload', 'none');
      wrapper.appendChild(clone);
    });
  }

  return true;
}

/**
 * Centered, endlessly looping Swiper — the shared setup for sections that show
 * a peeking carousel. Options are mobile-first: `spaceBetween` / `centeredSlides`
 * apply from 750px up, the `*Mobile` twins below it. Pass `scrollbar` to wire the
 * section's own scrollbar element:
 *
 *   window.initLoopSlider(el, { spaceBetween: 24, scrollbar: node })
 *     .then((swiper) => { ... });
 */
window.initLoopSlider = function initLoopSlider(element, options = {}) {
  const {
    spaceBetween = 0,
    spaceBetweenMobile = spaceBetween,
    centeredSlides = true,
    centeredSlidesMobile = centeredSlides,
    scrollbar,
    ...rest
  } = options;

  return window.loadSwiper().then((Swiper) => {
    element.swiper?.destroy(true, true);

    const isDesktop = window.matchMedia('(min-width: 750px)').matches;

    return new Swiper(element, {
      loop: fillLoopTrack(element, isDesktop ? spaceBetween : spaceBetweenMobile),
      slidesPerView: 'auto',
      loopAdditionalSlides: 2,
      spaceBetween: spaceBetweenMobile,
      centeredSlides: centeredSlidesMobile,
      breakpoints: { 750: { spaceBetween, centeredSlides } },
      // forceToAxis keeps a vertical trackpad gesture scrolling the page.
      mousewheel: { forceToAxis: true, releaseOnEdges: true },
      // Desktop gets wheel and trackpad only; touch devices keep finger swipes.
      simulateTouch: false,
      ...(scrollbar ? { scrollbar: { el: scrollbar, draggable: true } } : {}),
      ...rest
    });
  });
};

/* --- SECTIONS ------------------------------------------------------------- */

/**
 * Header: sticky behaviour, height variables, mobile drawer, megamenu and
 * predictive search. Not a custom element because the header markup is a
 * plain <header> owned by a section group.
 */
class Header {
  constructor(root) {
    this.root = root;
    this.mobileMenu = this.root.querySelector('#HeaderMenu');
    this.searchPanel = this.root.querySelector('#HeaderSearch');
    this.searchInput = this.root.querySelector('#HeaderSearchInput');
    this.searchResults = this.root.querySelector('#HeaderSearchResults');

    this.announcementsHeight = 0;
    this.searchAbortController = null;

    this.onClick = this.onClick.bind(this);
    this.onScroll = this.onScroll.bind(this);

    this.root.addEventListener('click', this.onClick);
    window.addEventListener('scroll', this.onScroll, { passive: true });

    this.observeHeight();
    this.initPredictiveSearch();
  }

  /**
   * Publishes --header-height and --menu-height so sections can offset
   * sticky elements without measuring the header themselves.
   */
  observeHeight() {
    const observer = new ResizeObserver(() => {
      const headerHeight = this.root.getBoundingClientRect().height;
      const announcements = document.querySelectorAll('.announcement');

      this.announcementsHeight = Array.from(announcements).reduce(
        (sum, el) => sum + el.offsetHeight,
        0
      );

      document.documentElement.style.setProperty('--header-height', `${headerHeight}px`);
      document.documentElement.style.setProperty(
        '--menu-height',
        `${headerHeight + this.announcementsHeight}px`
      );

      this.onScroll();
    });

    observer.observe(this.root);
  }

  onScroll() {
    this.root.classList.toggle('sticky', window.scrollY >= Math.max(this.announcementsHeight, 1));
  }

  onClick(event) {
    // Clicking the dimmed overlay of a drawer closes it.
    if (event.target === this.mobileMenu) return this.toggleMenu(false);
    if (event.target === this.searchPanel) return this.toggleSearch(false);

    const trigger = event.target.closest('[data-action]');
    if (!trigger || !this.root.contains(trigger)) return;

    switch (trigger.dataset.action) {
      case 'toggle-menu':
        this.toggleMenu();
        break;
      case 'close-menu':
        this.toggleMenu(false);
        break;
      case 'toggle-submenu':
        this.toggleSubmenu(trigger);
        break;
      case 'toggle-megamenu':
        this.toggleMegamenu(trigger);
        break;
      case 'close-megamenu':
        this.closeMegamenus();
        break;
      case 'toggle-search':
        this.toggleSearch();
        break;
      case 'close-search':
        this.toggleSearch(false);
        break;
      case 'reset-search':
        this.resetSearch();
        break;
    }
  }

  toggleMenu(force) {
    if (!this.mobileMenu) return;

    const willOpen = force === undefined ? !this.mobileMenu.classList.contains('open') : force;
    if (willOpen === this.mobileMenu.classList.contains('open')) return;

    this.mobileMenu.classList.toggle('open', willOpen);
    togglePageScroll(willOpen);
  }

  /** Accordion inside the mobile drawer. */
  toggleSubmenu(trigger) {
    const submenu = trigger.nextElementSibling;
    if (!submenu) return;

    const willOpen = !trigger.classList.contains('open');

    trigger.classList.toggle('open', willOpen);
    trigger.setAttribute('aria-expanded', willOpen);
    submenu.style.maxHeight = willOpen ? `${submenu.scrollHeight}px` : null;
  }

  /** Desktop megamenu — only one open at a time. */
  toggleMegamenu(trigger) {
    const item = trigger.closest('[data-menu-item]');
    if (!item) return;

    const willOpen = !item.classList.contains('open');

    this.closeMegamenus();
    item.classList.toggle('open', willOpen);
    trigger.setAttribute('aria-expanded', willOpen);
  }

  closeMegamenus() {
    this.root.querySelectorAll('[data-menu-item].open').forEach((item) => {
      item.classList.remove('open');
      item.querySelector('[data-action="toggle-megamenu"]')?.setAttribute('aria-expanded', 'false');
    });
  }

  toggleSearch(force) {
    if (!this.searchPanel) return;

    const willOpen = force === undefined ? !this.searchPanel.classList.contains('open') : force;
    if (willOpen === this.searchPanel.classList.contains('open')) return;

    this.searchPanel.classList.toggle('open', willOpen);
    togglePageScroll(willOpen);

    if (willOpen) this.searchInput?.focus();
  }

  resetSearch() {
    if (!this.searchInput) return;

    this.searchInput.value = '';
    this.searchInput.focus();
    this.renderSearchResults('');
  }

  initPredictiveSearch() {
    if (!this.searchInput || !this.searchResults) return;

    this.searchInput.addEventListener(
      'input',
      debounce((event) => this.onSearchInput(event.target.value), 500)
    );
  }

  onSearchInput(term) {
    const query = term.trim();

    if (query.length < 1) {
      this.renderSearchResults('');
      return;
    }

    this.searchAbortController?.abort();
    this.searchAbortController = new AbortController();

    this.searchResults.classList.add('loading');

    const url =
      `${window.Shopify.routes.root}search/suggest?q=${encodeURIComponent(query)}` +
      `&resources[type]=product&resources[limit]=4&section_id=predictive-results`;

    fetch(url, { signal: this.searchAbortController.signal })
      .then((response) => response.text())
      .then((body) => {
        const html = new DOMParser().parseFromString(body, 'text/html');
        this.renderSearchResults(html.querySelector('.predictive__content')?.innerHTML ?? '');
      })
      .catch((error) => {
        if (error.name !== 'AbortError') console.error(error);
      })
      .finally(() => this.searchResults.classList.remove('loading'));
  }

  renderSearchResults(html) {
    this.searchResults.innerHTML = html;
    this.searchResults.classList.toggle('has-results', html !== '');
  }
}

if (document.querySelector('.header')) {
  new Header(document.querySelector('.header'));
}

/* --- SCROLL ANIMATIONS ----------------------------------------------------
   Opt-in: put [js-animation] on a section and [js-animation-block] on the
   children that should stagger in. Styling lives in base.css.
   -------------------------------------------------------------------------- */

document.addEventListener('DOMContentLoaded', () => {
  initJsAnimations();
});

function initJsAnimations(userOpts = {}) {
  const opts = {
    sectionPxToTrigger: 150,
    cardThreshold: 0.2,
    sectionDuration: 0.7,
    cardDuration: 0.6,
    cardDelayStep: 0.3,
    rootMargin: '0px',
    once: true,
    ...userOpts
  };

  const sections = Array.from(document.querySelectorAll('[js-animation]'));
  if (!sections.length) return;

  sections.forEach((sec) => {
    const sectionPxToTrigger = getNumber(sec.dataset.sectionPx, opts.sectionPxToTrigger);
    const sectionDuration = getNumber(sec.dataset.sectionDuration, opts.sectionDuration);
    const cardDuration = getNumber(sec.dataset.cardDuration, opts.cardDuration);
    const cardDelayStep = getNumber(sec.dataset.cardDelayStep, opts.cardDelayStep);

    sec.style.setProperty('--section-dur', `${sectionDuration}s`);
    sec.style.setProperty('--card-dur', `${cardDuration}s`);
    sec.style.setProperty('--card-delay-step', `${cardDelayStep}s`);

    sec.__animOpts = { sectionPxToTrigger, cardDelayStep };
  });

  const cardObserver = new IntersectionObserver(onCardsIntersect, {
    root: null,
    rootMargin: opts.rootMargin,
    threshold: buildThresholds(opts.cardThreshold)
  });

  sections.forEach((sec) => {
    getCards(sec).forEach((card) => {
      card.__order = Number(card.getAttribute('js-animation-block')) || 0;
      card.__animated = false;
      cardObserver.observe(card);
    });
  });

  const pending = new Set(sections);
  let ticking = false;

  function checkSections() {
    ticking = false;
    const viewportH = window.innerHeight || document.documentElement.clientHeight || 0;

    pending.forEach((sec) => {
      const rect = sec.getBoundingClientRect();

      if (rect.bottom <= 0 || rect.top >= viewportH) {
        if (!opts.once) resetSection(sec);
        return;
      }

      const needPx = Math.min(sec.__animOpts.sectionPxToTrigger, rect.height);
      const firstChunkVisible = rect.top <= viewportH - needPx;

      if (firstChunkVisible) {
        sec.classList.add('is-animated');
        if (opts.once) pending.delete(sec);
      } else if (!opts.once) {
        resetSection(sec);
      }
    });
  }

  function resetSection(sec) {
    sec.classList.remove('is-animated');
    getCards(sec).forEach((c) => {
      c.__animated = false;
      c.classList.remove('is-animated');
      c.style.transitionDelay = '';
    });
  }

  function onScrollOrResize() {
    if (!ticking) {
      ticking = true;
      requestAnimationFrame(checkSections);
    }
  }

  onScrollOrResize();
  window.addEventListener('scroll', onScrollOrResize, { passive: true });
  window.addEventListener('resize', onScrollOrResize);

  let rafScheduled = false;
  const pendingVisibleBySection = new Map();

  function onCardsIntersect(entries) {
    for (const entry of entries) {
      const card = entry.target;
      const sec = card.closest('[js-animation]');
      if (!sec) continue;

      const isVisible = entry.isIntersecting && entry.intersectionRatio > 0;

      if (isVisible && !card.__animated) {
        if (!pendingVisibleBySection.has(sec)) pendingVisibleBySection.set(sec, new Set());
        pendingVisibleBySection.get(sec).add(card);
      }

      if (!isVisible && !opts.once && card.__animated) {
        card.__animated = false;
        card.classList.remove('is-animated');
        card.style.transitionDelay = '';
      }
    }

    if (!rafScheduled) {
      rafScheduled = true;
      requestAnimationFrame(flushVisibleBatches);
    }
  }

  function flushVisibleBatches() {
    rafScheduled = false;

    for (const [sec, setOfCards] of pendingVisibleBySection.entries()) {
      if (!sec.classList.contains('is-animated')) continue;

      const delayStep = sec.__animOpts?.cardDelayStep ?? opts.cardDelayStep;

      Array.from(setOfCards)
        .filter((c) => !c.__animated)
        .sort((a, b) => (a.__order || 0) - (b.__order || 0))
        .forEach((card, idx) => {
          card.style.transitionDelay = `${idx * delayStep}s`;
          card.classList.add('is-animated');
          card.__animated = true;
        });

      setOfCards.clear();
    }
  }

  function getCards(sectionEl) {
    return Array.from(sectionEl.querySelectorAll('[js-animation-block]'));
  }

  function buildThresholds(minRatio) {
    const arr = [];
    for (let i = 0; i <= 10; i++) arr.push(i / 10);
    if (!arr.includes(minRatio)) arr.push(minRatio);
    return arr.sort((a, b) => a - b);
  }

  function getNumber(val, fallback) {
    const n = Number(val);
    return Number.isFinite(n) ? n : fallback;
  }
}

/**
 * <featured-products> — a product carousel. With data-type="recommendations"
 * it replaces its own grid with results from the recommendations API.
 */
class FeaturedProduct extends HTMLElement {
  connectedCallback() {
    if (this.dataset.type === 'recommendations') {
      this.setRecommendationProducts();
    }
  }

  setRecommendationProducts() {
    const url =
      `${window.Shopify.routes.root}recommendations/products` +
      `?limit=4&product_id=${this.dataset.product}` +
      `&intent=${this.dataset.recommendations}&section_id=${this.dataset.section}`;

    fetchDocument(url)
      .then((html) => {
        if (html.querySelector('featured-products[data-empty]')) {
          this.style.display = 'none';
          return;
        }

        const grid = this.querySelector('[id^="Slider-"]');
        const newGrid = html.querySelector('[id^="Slider-"]');
        if (!grid || !newGrid) return;

        grid.innerHTML = newGrid.innerHTML;
        this.querySelector('slider-component')?.update();
      })
      .catch((error) => console.error(error));
  }
}

if (document.querySelector('featured-products')) {
  customElements.define('featured-products', FeaturedProduct);
}

/**
 * <main-product> — gallery, variant picker, add to cart, sticky bar,
 * disclosures, lightbox and (optionally) a product switcher that swaps the
 * whole page without a reload.
 */
class MainProduct extends HTMLElement {
  constructor() {
    super();
    this.isLoading = false;
    this.cart = document.querySelector('cart-drawer');
    this.alternatePages = null;
    this.alternatePagesUrls = [];
  }

  connectedCallback() {
    const variantsData = this.querySelector('[data-variants-data]');
    this.productVariants = variantsData ? JSON.parse(variantsData.textContent) : [];

    this.productGallery = this.querySelector('[id^="ProductGallery-"]');
    this.productOptions = this.querySelector('[id^="ProductOptions-"]');
    this.productQuantityOptions = this.querySelector('[id^="ProductQuantityOptions-"]');
    this.productForm = this.querySelector('[id^="ProductForm-"]');

    this.handleGalleryUpdate = this.updateGallery.bind(this);
    this.handleVariantChange = this.onVariantChange.bind(this);
    this.handleQuantityChange = this.onQuantityChange.bind(this);
    this.handleFormSubmit = this.onFormSubmit.bind(this);
    this.handleClick = this.onClick.bind(this);
    this.handleScroll = this.updateStickyBar.bind(this);

    this.productGallery?.addEventListener('slider-updated', this.handleGalleryUpdate);
    this.productOptions?.addEventListener('change', this.handleVariantChange);
    this.productQuantityOptions?.addEventListener('change', this.handleQuantityChange);
    this.productForm?.addEventListener('submit', this.handleFormSubmit);
    this.addEventListener('click', this.handleClick);

    if (this.querySelector('[id^="ProductSticky-"]')) {
      window.addEventListener('scroll', this.handleScroll, { passive: true });
      this.updateStickyBar();
    }

    if (this.querySelectorAll('[data-page]').length > 1 && !this.alternatePages) {
      this.fetchPages();
    }

    setViewedProducts(this.dataset.handle);
  }

  disconnectedCallback() {
    this.productGallery?.removeEventListener('slider-updated', this.handleGalleryUpdate);
    this.productOptions?.removeEventListener('change', this.handleVariantChange);
    this.productQuantityOptions?.removeEventListener('change', this.handleQuantityChange);
    this.productForm?.removeEventListener('submit', this.handleFormSubmit);
    this.removeEventListener('click', this.handleClick);
    window.removeEventListener('scroll', this.handleScroll);
  }

  /* --- product switcher --- */

  fetchPages() {
    this.alternatePagesUrls = Array.from(this.querySelectorAll('[data-page]')).map(
      (toggle) => toggle.dataset.page
    );

    Promise.all(this.alternatePagesUrls.map(fetchDocument))
      .then((htmlArray) => {
        this.alternatePages = htmlArray;
      })
      .catch((error) => console.error(error));
  }

  changePage(url) {
    if (!this.alternatePages) return;

    const index = this.alternatePagesUrls.indexOf(url);
    if (index === -1) return;

    const newProductSection = this.alternatePages[index].querySelector('main-product');
    if (!newProductSection) return;

    this.disconnectedCallback();
    this.innerHTML = newProductSection.innerHTML;
    this.className = newProductSection.className;
    this.connectedCallback();

    if (this.dataset.template !== 'product') return;

    window.history.replaceState({}, '', window.location.origin + this.alternatePagesUrls[index]);

    // Swap the rest of the template too, skipping sections that own state.
    const newSections = this.alternatePages[index].querySelectorAll('main .shopify-section');
    const currentSections = document.querySelectorAll('main .shopify-section');
    const excluded = 'main-product';

    currentSections.forEach((section, i) => {
      const newSection = newSections[i];
      if (!newSection || section.querySelector(excluded)) return;

      section.innerHTML = newSection.innerHTML;

      section.querySelectorAll('script').forEach((oldScript) => {
        if (oldScript.src) return;
        const newScript = document.createElement('script');
        newScript.textContent = oldScript.textContent;
        section.appendChild(newScript);
        oldScript.remove();
      });
    });
  }

  /* --- cart --- */

  onFormSubmit(event) {
    event?.preventDefault();
    if (this.isLoading) return;

    const idInput = this.querySelector("[name='id']");
    const quantityInput = this.querySelector("[name='quantity']");
    if (!idInput) return;

    this.addToCart({
      items: [
        {
          id: parseInt(idInput.value, 10),
          quantity: parseInt(quantityInput?.value ?? 1, 10)
        }
      ],
      sections: this.cart ? ['cart-drawer'] : [],
      sections_url: window.location.pathname
    });
  }

  addToCart(data) {
    if (!data) return;

    this.toggleLoading(true);

    fetch(`${window.Shopify.routes.root}cart/add.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data)
    })
      .then((response) => response.json())
      .then((response) => {
        if (!response.items) throw new Error(response.description);

        if (this.cart) {
          this.cart.renderContent(response.sections);
          this.cart.open();
        }
      })
      .catch((error) => this.showError(error))
      .finally(() => this.toggleLoading(false));
  }

  /* --- quantity options --- */

  /**
   * Pack size cards. The option carries the quantity plus its own display price,
   * so picking one only swaps text and the quantity the form submits — the
   * variant never changes.
   */
  onQuantityChange() {
    const selected = this.productQuantityOptions?.querySelector('input:checked');
    if (!selected) return;

    const quantityInput = this.querySelector("[name='quantity']");
    if (quantityInput) quantityInput.value = selected.value;

    this.querySelectorAll('[data-content="price"]').forEach((el) => {
      el.innerHTML = selected.dataset.price || '';
    });

    this.querySelectorAll('[data-content="compare"]').forEach((el) => {
      el.innerHTML = selected.dataset.compare || '';
    });
  }

  /* --- variants --- */

  onVariantChange() {
    if (this.isLoading) return;

    this.toggleLoading(true);

    const selectedValues = [];

    this.querySelectorAll('[data-option]').forEach((optionForm) => {
      const checked = optionForm.querySelector('input:checked') || optionForm.querySelector('input');
      if (checked) selectedValues.push(checked.value);
    });

    const searchParams = new URLSearchParams(window.location.search);
    const matched = this.productVariants.find(
      (variant) => variant.title === selectedValues.join(' / ')
    );

    if (matched) {
      const idInput = this.querySelector("input[name='id']");
      if (idInput) idInput.value = matched.id;
      searchParams.set('variant', matched.id);
    }

    const newProductUrl = `${window.location.pathname}?${searchParams.toString()}`;
    window.history.replaceState({}, '', newProductUrl);

    fetchDocument(newProductUrl)
      .then((html) => this.renderContent(html))
      .catch((error) => this.showError(error))
      .finally(() => this.toggleLoading(false));
  }

  renderContent(html) {
    const section = html.querySelector(`[data-section="${this.dataset.section}"]`);
    if (!section) return;

    const selectors = [
      '[id^="ProductPrice-"]',
      '[id^="ProductOptions-"]',
      '[id^="ProductSticky-"]',
      '[id^="ProductSubmit-"]',
      '[data-inventory]'
    ];

    selectors.forEach((selector) => {
      const current = this.querySelectorAll(selector);
      const updated = section.querySelectorAll(selector);
      if (!current.length || current.length !== updated.length) return;

      current.forEach((el, i) => {
        el.innerHTML = updated[i].innerHTML;
      });
    });

    const currentButton = this.querySelector('[id^="ProductSubmit-"]');
    const newButton = section.querySelector('[id^="ProductSubmit-"]');
    if (currentButton && newButton) {
      currentButton.toggleAttribute('disabled', newButton.hasAttribute('disabled'));
    }
  }

  /* --- interactions --- */

  onClick(event) {
    const trigger = event.target.closest('[data-action]');
    if (!trigger || !this.contains(trigger)) return;

    switch (trigger.dataset.action) {
      case 'select-slide':
        this.productGallery
          ?.querySelector('slider-component')
          ?.scrollToSlide(trigger.dataset.index);
        break;

      case 'open-lightbox': {
        const lightbox = this.querySelector('[id^="ProductLightbox-"]');
        if (!lightbox) break;

        const gallerySlider = this.productGallery?.querySelector('slider-component');
        if (gallerySlider) {
          lightbox.querySelector('slider-component')?.scrollToSlide(gallerySlider.currentSlide);
        }

        lightbox.classList.add('open');
        togglePageScroll(true);
        break;
      }

      case 'close-lightbox': {
        this.querySelector('[id^="ProductLightbox-"]')?.classList.remove('open');
        togglePageScroll(false);
        break;
      }

      case 'toggle-disclosure': {
        const content = this.querySelector(
          `[data-disclosure-content="${trigger.dataset.disclosure}"]`
        );
        if (!content) break;

        const willOpen = !trigger.classList.contains('open');
        trigger.classList.toggle('open', willOpen);
        trigger.setAttribute('aria-expanded', willOpen);
        content.style.maxHeight = willOpen ? `${content.scrollHeight}px` : null;
        break;
      }

      case 'video-play': {
        const video = trigger.closest('[id^="Slide-"]')?.querySelector('[data-product-video]');
        if (!video) break;

        if (video.paused) {
          video.play().catch(() => {});
        } else {
          video.pause();
        }

        this.updateVideoState(video);
        break;
      }

      case 'video-mute': {
        const video = trigger.closest('[id^="Slide-"]')?.querySelector('[data-product-video]');
        if (!video) break;

        video.muted = !video.muted;
        this.updateVideoState(video);
        break;
      }

      case 'add-to-cart':
        this.onFormSubmit();
        break;

      case 'add-product':
        this.addToCart({
          items: [{ id: parseInt(trigger.dataset.variant, 10), quantity: 1 }],
          sections: this.cart ? ['cart-drawer'] : [],
          sections_url: window.location.pathname
        });
        this.closePopup(trigger);
        break;

      case 'close-popup':
        this.closePopup(trigger);
        break;

      case 'switch-product':
        this.changePage(trigger.dataset.page);
        break;
    }
  }

  closePopup(trigger) {
    const popup = trigger.closest('[data-popup]');
    if (!popup) return;

    popup.classList.remove('open');
    togglePageScroll(false);
  }

  updateStickyBar() {
    const stickyBar = this.querySelector('[id^="ProductSticky-"]');
    const submit = this.querySelector('[id^="ProductSubmit-"]');
    if (!stickyBar || !submit) return;

    // Show the bar only once the real add-to-cart button has scrolled past.
    stickyBar.classList.toggle('show', submit.getBoundingClientRect().bottom < 0);
  }

  updateGallery(event) {
    this.querySelectorAll('[id^="Slider-GalleryNav-"] li').forEach((slide) => {
      slide.classList.toggle('is-selected', event.detail.currentSlide === parseInt(slide.dataset.index, 10));
    });

    // Only the slide in view plays; the rest rewind and wait.
    this.querySelectorAll('[data-product-video]').forEach((video) => {
      const slide = video.closest('[id^="Slide-"]');
      const isCurrent = event.detail.currentSlide === parseInt(slide?.dataset.index, 10);

      if (isCurrent) {
        video.play().catch(() => {});
      } else {
        video.pause();
        video.currentTime = 0;
      }

      this.updateVideoState(video);
    });
  }

  /** Mirrors the video's own state onto the slide, so CSS can swap the icons. */
  updateVideoState(video) {
    const slide = video.closest('[id^="Slide-"]');
    if (!slide) return;

    slide.classList.toggle('video-playing', !video.paused);
    slide.classList.toggle('video-muted', video.muted);
  }

  showError(error) {
    console.error(error);

    const messageBlock = this.querySelector('[product-notification]');
    if (!messageBlock) return;

    messageBlock.innerText = error.message;
    setTimeout(() => {
      messageBlock.innerText = '';
    }, 5000);
    messageBlock.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }

  toggleLoading(state) {
    this.isLoading = state ?? !this.isLoading;
    this.classList.toggle('loading', this.isLoading);
  }
}

if (document.querySelector('main-product')) {
  customElements.define('main-product', MainProduct);
}

/**
 * <main-collection> — sorting, filtering and pagination without a reload.
 * All three funnel into filtersUpdate() -> renderContent().
 */
class MainCollection extends HTMLElement {
  constructor() {
    super();
    this.isLoading = false;
  }

  connectedCallback() {
    this.currentPage = parseInt(this.dataset.page, 10) || 1;

    this.handleClick = this.onClick.bind(this);
    this.handleOutsideClick = this.onOutsideClick.bind(this);
    this.handleFilterChange = this.onFilterChange.bind(this);
    this.handlePopState = this.onPopState.bind(this);

    this.addEventListener('click', this.handleClick);
    this.addEventListener('change', this.handleFilterChange);
    window.addEventListener('click', this.handleOutsideClick);
    window.addEventListener('popstate', this.handlePopState);

    this.toggleApply();
    this.initDisclosures();
  }

  /** Filter groups render expanded, so their measured height is set up front. */
  initDisclosures() {
    this.querySelectorAll('[data-filter].open').forEach((filter) => {
      toggleDisclosure(filter.querySelector('[data-filter-content]'), true, false);
    });
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.handleClick);
    this.removeEventListener('change', this.handleFilterChange);
    window.removeEventListener('click', this.handleOutsideClick);
    window.removeEventListener('popstate', this.handlePopState);
  }

  onClick(event) {
    // A click on the dimmed area next to the drawer closes it.
    if (event.target.matches('[data-filters-drawer].open')) {
      event.target.classList.remove('open');
      togglePageScroll(false);
      return;
    }

    const trigger = event.target.closest('[data-action]');
    if (!trigger || !this.contains(trigger)) return;

    switch (trigger.dataset.action) {
      case 'open-options':
        this.querySelector('[data-sort]')?.classList.toggle('open');
        break;

      case 'select-option': {
        const sortInput = this.querySelector("[name='sort_by']");
        if (sortInput) sortInput.value = trigger.dataset.option;

        this.querySelector('[data-sort] .active')?.classList.remove('active');
        trigger.classList.add('active');
        this.querySelector('[data-sort]')?.classList.remove('open');
        this.filtersUpdate();
        break;
      }

      // Tabs stay real links, but swap the grid in place instead of navigating.
      case 'switch-collection': {
        event.preventDefault();
        const handle = trigger.dataset.collection;
        if (!handle) break;

        const params = new URLSearchParams();
        const sortValue = this.querySelector("[name='sort_by']")?.value;
        if (sortValue) params.set('sort_by', sortValue);

        const query = params.toString();
        const root = window.Shopify?.routes?.root || '/';
        this.filtersUpdate(`${root}collections/${handle}${query.length ? `?${query}` : ''}`);
        break;
      }

      case 'toggle-filter': {
        const filter = trigger.closest('[data-filter]');
        if (!filter) break;

        const willOpen = !filter.classList.contains('open');
        filter.classList.toggle('open', willOpen);
        trigger.setAttribute('aria-expanded', willOpen);
        toggleDisclosure(filter.querySelector('[data-filter-content]'), willOpen);
        break;
      }

      case 'toggle-filters-drawer':
        this.querySelector('[data-filters-drawer]')?.classList.toggle('open');
        togglePageScroll();
        break;

      case 'apply-filters':
        event.preventDefault();
        this.querySelector('[data-filters-drawer].open')?.classList.remove('open');
        togglePageScroll(false);
        this.filtersUpdate();
        break;

      case 'clear-filters':
        event.preventDefault();
        this.filtersUpdate(trigger.getAttribute('href'));
        break;

      case 'paginate':
        event.preventDefault();
        this.filtersUpdate(trigger.getAttribute('href'));
        break;

      case 'load-more':
        event.preventDefault();
        this.loadMore();
        break;
    }
  }

  onOutsideClick(event) {
    if (event.target.closest('[data-sort]')) return;
    this.querySelector('[data-sort]')?.classList.remove('open');
  }

  /**
   * Ticking a box only arms the apply button — the grid is refetched when the
   * shopper confirms, not on every change.
   */
  onFilterChange(event) {
    if (!event.target.closest('[data-filters-form]')) return;
    this.toggleApply();
  }

  toggleApply() {
    const form = this.querySelector('[data-filters-form]');
    if (!form) return;

    const hasValue = form.querySelector('input[type="checkbox"]:checked') !== null;
    this.querySelectorAll('[data-action="apply-filters"]').forEach((button) => {
      button.disabled = !hasValue;
    });
  }

  /**
   * @param {string|false} url Explicit URL (pagination / clear filters), or
   *                           false to rebuild it from the current form state.
   */
  filtersUpdate(url = false) {
    let newUrl = url;

    if (!newUrl) {
      const form = this.querySelector('[data-filters-form]');
      const params = form
        ? new URLSearchParams(new FormData(form))
        : new URLSearchParams(window.location.search);

      const sortInput = this.querySelector("[name='sort_by']");
      if (sortInput) params.set('sort_by', sortInput.value);
      params.delete('page');

      newUrl = `${window.location.pathname}?${params.toString()}`;
    }

    // pushState, so the back button walks through the applied filters.
    window.history.pushState({}, '', newUrl);
    this.fetchAndRender(newUrl);
  }

  onPopState() {
    this.fetchAndRender(window.location.href);
  }

  /** Re-renders in place — the shopper keeps their scroll position. */
  fetchAndRender(url) {
    this.toggleLoading(true);
    this.currentPage = 1;

    fetchDocument(url)
      .then((html) => this.renderContent(html))
      .catch((error) => console.error(error))
      .finally(() => this.toggleLoading(false));
  }

  /**
   * Appends the next page to the grid instead of replacing it. The pagination
   * region comes from the fetched page, so the button disappears on the last one.
   */
  loadMore() {
    this.toggleLoading(true);
    this.currentPage += 1;

    const params = new URLSearchParams(window.location.search);
    params.set('page', this.currentPage);

    fetchDocument(`${window.location.pathname}?${params.toString()}`)
      .then((html) => {
        const section = html.querySelector(`[data-section="${this.dataset.section}"]`);
        const grid = this.querySelector('[id^="CollectionGrid-"]');
        const nextGrid = section?.querySelector('[id^="CollectionGrid-"]');
        if (!grid || !nextGrid) return;

        grid.append(...Array.from(nextGrid.children));
        this.renderContent(html, '[data-grid]');
      })
      .catch((error) => console.error(error))
      .finally(() => this.toggleLoading(false));
  }

  /** @param {string|false} exclusion Region to leave untouched (load more keeps the grid). */
  renderContent(html, exclusion = false) {
    const section = html.querySelector(`[data-section="${this.dataset.section}"]`);
    if (!section) return;

    ['[data-headings]', '[data-filters]', '[data-grid]', '[data-pagination]'].forEach((selector) => {
      if (selector === exclusion) return;

      const current = this.querySelectorAll(selector);
      const updated = section.querySelectorAll(selector);
      if (!current.length || current.length !== updated.length) return;

      current.forEach((el, i) => {
        el.innerHTML = updated[i].innerHTML;
      });
    });

    this.toggleApply();
    this.initDisclosures();
  }

  toggleLoading(state) {
    this.isLoading = state ?? !this.isLoading;
    this.classList.toggle('loading', this.isLoading);
  }
}

if (document.querySelector('main-collection')) {
  customElements.define('main-collection', MainCollection);
}

/** <main-search> — same sorting/pagination contract as the collection. */
class MainSearch extends MainCollection {
  onClick(event) {
    const trigger = event.target.closest('[data-action]');

    if (trigger?.dataset.action === 'clear-search') {
      const input = this.querySelector('[data-search-input]');
      if (input) {
        input.value = '';
        input.focus();
      }
      return;
    }

    super.onClick(event);
  }
}

if (document.querySelector('main-search')) {
  customElements.define('main-search', MainSearch);
}

/** <main-blog> — tab switching between blogs plus pagination. */
class MainBlog extends MainCollection {
  connectedCallback() {
    super.connectedCallback();

    this.articles = this.querySelector('[id^="CollectionGrid-"]');
    this.handleArticlesScroll = this.onArticlesScroll.bind(this);
    this.articles?.addEventListener('scroll', this.handleArticlesScroll, { passive: true });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.articles?.removeEventListener('scroll', this.handleArticlesScroll);
  }

  /**
   * Mobile swaps the grid for a slider and drops the load more button, so the
   * next page is pulled in as the shopper nears the end of the track. On
   * desktop the grid doesn't scroll sideways and this never fires.
   */
  onArticlesScroll() {
    if (this.isLoading || !this.querySelector('[data-action="load-more"]')) return;

    const { scrollLeft, clientWidth, scrollWidth } = this.articles;
    if (scrollLeft + clientWidth < scrollWidth - 200) return;

    this.loadMore();
  }

  onClick(event) {
    const trigger = event.target.closest('[data-action]');

    if (trigger?.dataset.action === 'switch-blog') {
      this.querySelector('[data-tabs] .active')?.classList.remove('active');
      trigger.classList.add('active');
      this.filtersUpdate(`${window.Shopify.routes.root}blogs/${trigger.dataset.blog}`);
      return;
    }

    super.onClick(event);
  }
}

if (document.querySelector('main-blog')) {
  customElements.define('main-blog', MainBlog);
}

/* --- COMPONENTS ----------------------------------------------------------- */

/**
 * <slider-component> — native scroll-snap carousel. Reach for Swiper only
 * when you need loop/autoplay/nested sliders (see window.loadSwiper).
 *
 * Markup contract:
 *   <slider-component>
 *     <ul id="Slider-xxx"><li id="Slide-xxx-1">…</li></ul>
 *     <div slider-controls><button name="previous"><button name="next"></div>
 *     <div slider-scrollbar><span></span></div>
 *     <div slider-pagination></div>
 *     <span slider-counter></span>
 *   </slider-component>
 */
class SliderComponent extends HTMLElement {
  connectedCallback() {
    this.currentSlide = 1;
    this.allowEvents = this.getAttribute('data-allow-events') === 'true';
    this.hideControls = this.getAttribute('data-hide-controls') !== 'false';

    this.slider = this.querySelector('[id^="Slider-"]');
    if (!this.slider) return;

    this.prevButton = this.querySelector('button[name="previous"]');
    this.nextButton = this.querySelector('button[name="next"]');
    this.scrollbar = this.querySelector('[slider-scrollbar]');
    this.scrollbarThumb = this.querySelector('[slider-scrollbar] span');
    this.pagination = this.querySelector('[slider-pagination]');
    this.counter = this.querySelector('[slider-counter]');

    this.handleUpdate = this.update.bind(this);
    this.handleButtonClick = this.onButtonClick.bind(this);
    this.handleDotClick = this.onDotClick.bind(this);

    this.update();
    window.addEventListener('resize', this.handleUpdate);
    this.slider.addEventListener('scroll', this.handleUpdate, { passive: true });

    this.prevButton?.addEventListener('click', this.handleButtonClick);
    this.nextButton?.addEventListener('click', this.handleButtonClick);
    this.pagination?.addEventListener('click', this.handleDotClick);
  }

  disconnectedCallback() {
    window.removeEventListener('resize', this.handleUpdate);
    this.slider?.removeEventListener('scroll', this.handleUpdate);
  }

  onButtonClick(event) {
    event.preventDefault();

    if (event.currentTarget.name === 'previous' && this.currentSlide > 1) {
      this.currentSlide -= 1;
    } else if (event.currentTarget.name === 'next' && this.currentSlide < this.maxLength) {
      this.currentSlide += 1;
    }

    this.scrollToSlide(this.currentSlide);
  }

  onDotClick(event) {
    event.preventDefault();
    if (!event.target.hasAttribute('data-slide')) return;

    this.scrollToSlide(parseInt(event.target.dataset.slide, 10));
  }

  scrollToSlide(index) {
    const slide = this.querySelector(`[id^="Slide-"]:nth-child(${index})`);
    if (!slide) return;

    this.slider.scrollTo({ left: slide.offsetLeft - this.slider.offsetLeft, behavior: 'smooth' });
  }

  update() {
    const items = this.querySelectorAll('[id^="Slide-"]');
    if (!items.length) return;

    const { scrollLeft, scrollWidth, clientWidth } = this.slider;
    const blockWidth = items[0].offsetWidth || 1;
    const gap = parseFloat(getComputedStyle(this.slider).columnGap) || 0;
    const hasScroll = scrollWidth > clientWidth + 1;

    this.maxLength = Math.max(1, items.length - (Math.floor(clientWidth / blockWidth) - 1));
    this.currentSlide = Math.round(scrollLeft / (blockWidth + gap)) + 1;

    items.forEach((item, index) => item.classList.toggle('active', index + 1 === this.currentSlide));

    if (this.prevButton && this.nextButton) {
      this.prevButton.toggleAttribute('disabled', this.currentSlide === 1);
      this.nextButton.toggleAttribute('disabled', this.currentSlide === this.maxLength);

      if (this.hideControls) {
        this.prevButton.classList.toggle('hidden', !hasScroll);
        this.nextButton.classList.toggle('hidden', !hasScroll);
      }
    }

    if (this.scrollbarThumb) {
      const thumbWidth = (clientWidth / scrollWidth) * 100;
      const thumbPosition = (scrollLeft / (scrollWidth - clientWidth)) * 100;

      this.scrollbarThumb.style.left = `${thumbPosition}%`;
      this.scrollbarThumb.style.width = `${thumbWidth}%`;
      this.scrollbarThumb.style.transform = `translateX(-${thumbPosition}%)`;
      this.scrollbar?.classList.toggle('hidden', !hasScroll);
    }

    if (this.pagination) this.updatePagination(items);
    if (this.counter) this.counter.innerHTML = `${this.currentSlide} / ${items.length}`;

    if (this.allowEvents) {
      this.dispatchEvent(
        new CustomEvent('slider-updated', {
          detail: { currentSlide: this.currentSlide },
          bubbles: true
        })
      );
    }
  }

  updatePagination(slides) {
    const slidesPerPage = Math.max(1, Math.floor(this.slider.clientWidth / slides[0].offsetWidth));
    const totalPages = slides.length - (slidesPerPage - 1);

    this.pagination.innerHTML = '';
    this.pagination.classList.toggle('hidden', totalPages <= 1);

    for (let i = 1; i <= totalPages; i++) {
      const dot = document.createElement('span');
      dot.setAttribute('data-slide', i);
      if (i === this.currentSlide) dot.classList.add('active');
      this.pagination.appendChild(dot);
    }
  }
}

customElements.define('slider-component', SliderComponent);

/**
 * <product-card> — colour/sibling swapping and quick add.
 * Sibling radios carry a JSON payload in data-details.
 */
class ProductCard extends HTMLElement {
  connectedCallback() {
    this.cart = document.querySelector('cart-drawer');
    this.siblingsForm = this.querySelector('[id^="Siblings-"]');

    this.handleSiblingChange = this.changeSibling.bind(this);
    this.handleClick = this.onClick.bind(this);

    this.siblingsForm?.addEventListener('change', this.handleSiblingChange);
    this.addEventListener('click', this.handleClick);
  }

  disconnectedCallback() {
    this.siblingsForm?.removeEventListener('change', this.handleSiblingChange);
    this.removeEventListener('click', this.handleClick);
  }

  onClick(event) {
    const trigger = event.target.closest('[data-action]');
    if (!trigger || !this.contains(trigger)) return;

    if (trigger.dataset.action === 'quick-add') {
      event.preventDefault();
      this.quickAdd(trigger);
    }
  }

  quickAdd(trigger) {
    const variantId = trigger.dataset.variant;
    if (!variantId || this.classList.contains('loading')) return;

    this.classList.add('loading');

    // Without a drawer in the DOM the item is still added — just silently.
    const request = this.cart
      ? this.cart.addItemToCart(variantId).then(() => this.cart.open())
      : addVariantToCart(variantId);

    request.finally(() => this.classList.remove('loading'));
  }

  changeSibling(event) {
    const activeMedia = this.querySelector('[data-sibling-media].active');
    const nextMedia = this.querySelector(`[data-sibling-media="${event.target.value}"]`);

    if (activeMedia && nextMedia) {
      activeMedia.classList.remove('active');
      nextMedia.classList.add('active');
    }

    const activeTags = this.querySelector('[data-sibling-tags].active');
    const nextTags = this.querySelector(`[data-sibling-tags="${event.target.value}"]`);

    if (activeTags) activeTags.classList.remove('active');
    if (nextTags) nextTags.classList.add('active');

    let details;
    try {
      details = JSON.parse(event.target.dataset.details);
    } catch (error) {
      return;
    }

    const title = this.querySelector('[data-title]');
    if (title) {
      title.href = details.product_url;
      title.innerText = details.title;
    }

    const subtitle = this.querySelector('[data-subtitle]');
    if (subtitle) subtitle.innerHTML = details.subtitle ?? '';

    const price = this.querySelector('[data-price]');
    if (price) price.innerHTML = details.price ?? '';

    const compare = this.querySelector('[data-compare]');
    if (compare) compare.innerHTML = details.compare ?? '';

    const quickAdd = this.querySelector('[data-action="quick-add"]');
    if (quickAdd) quickAdd.dataset.variant = details.id;
  }
}

customElements.define('product-card', ProductCard);

/**
 * <cart-drawer> — rendered once in layout/theme.liquid and refreshed through
 * the Section Rendering API (`sections: ['cart-drawer']`).
 *
 * Markup contract: [js-content-items], [js-content-footer], data-count,
 * [data-item], [name="quantity"], [item-notification], plus [data-gift-state]
 * and [data-gift-item] for the free gift.
 */
class CartDrawer extends HTMLElement {
  constructor() {
    super();
    this.isLoading = false;
  }

  connectedCallback() {
    this.addEventListener('click', this.onClick.bind(this));

    document.addEventListener('click', (event) => {
      if (event.target.closest('[cart-drawer-toggle]')) {
        event.preventDefault();
        this.open();
      }
    });

    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && this.classList.contains('open')) this.close();
    });

    this.syncGift();
    this.syncDiscounts();
  }

  onClick(event) {
    // Clicking the dimmed overlay (the host itself) closes the drawer.
    if (event.target === this) return this.close();

    const trigger = event.target.closest('[data-action]');
    if (!trigger || !this.contains(trigger)) return;

    const item = trigger.closest('[data-item]');
    const quantityInput = item?.querySelector('[name="quantity"]');
    const quantity = quantityInput ? parseInt(quantityInput.value, 10) : 0;

    switch (trigger.dataset.action) {
      case 'close':
        this.close();
        break;

      case 'remove-item':
        if (!this.isLoading && item) {
          if (item.hasAttribute('data-gift-item')) this.setGiftDismissed(true);
          this.updateQuantity(parseInt(item.dataset.item, 10), 0, item);
        }
        break;

      case 'decrease-quantity':
        if (!this.isLoading && item && quantity > 1) {
          this.updateQuantity(parseInt(item.dataset.item, 10), quantity - 1, item);
        }
        break;

      case 'increase-quantity':
        if (!this.isLoading && item && quantity < 199) {
          this.updateQuantity(parseInt(item.dataset.item, 10), quantity + 1, item);
        }
        break;

      case 'add-product':
        if (!this.isLoading) this.addItemToCart(trigger.dataset.variant, trigger);
        break;

      case 'to-checkout':
        window.location.href = `${window.Shopify.routes.root}checkout`;
        break;
    }
  }

  updateQuantity(line, quantity, itemEl = false) {
    this.toggleLoading(true, itemEl);

    // cart/update.js takes every line at once, so the pack-size discount codes
    // can be reconciled in the same request as the new quantity.
    const updates = [...this.querySelectorAll('[data-item]')].map((el) => {
      const currentLine = parseInt(el.dataset.item, 10);
      const currentQuantity = parseInt(el.querySelector('[name="quantity"]')?.value ?? el.dataset.quantity, 10);
      return currentLine === line ? quantity : currentQuantity;
    });

    return fetch(`${window.Shopify.routes.root}cart/update.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        updates,
        discount: this.computeTargetDiscounts({ [line]: quantity }).join(','),
        sections: ['cart-drawer'],
        sections_url: window.location.pathname
      })
    })
      .then((response) => response.json())
      .then((response) => {
        if (!response.sections) throw new Error(response.description);
        this.renderContent(response.sections);
      })
      .catch((error) => this.showError(error, line))
      .finally(() => this.toggleLoading(false, itemEl));
  }

  addItemToCart(variantId, itemEl = false) {
    this.toggleLoading(true, itemEl);

    return fetch(`${window.Shopify.routes.root}cart/add.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        items: [{ id: parseInt(variantId, 10), quantity: 1 }],
        sections: ['cart-drawer'],
        sections_url: window.location.pathname
      })
    })
      .then((response) => response.json())
      .then((response) => {
        if (!response.items) throw new Error(response.description);
        this.renderContent(response.sections);
      })
      .catch((error) => console.error(error))
      .finally(() => this.toggleLoading(false, itemEl));
  }

  renderContent(sections) {
    if (!sections || !sections['cart-drawer']) return;

    const html = new DOMParser().parseFromString(sections['cart-drawer'], 'text/html');
    const newDrawer = html.querySelector('cart-drawer');
    if (!newDrawer) return;

    ['[js-content-items]', '[js-content-footer]'].forEach((selector) => {
      const current = this.querySelector(selector);
      const updated = newDrawer.querySelector(selector);
      if (current && updated) current.innerHTML = updated.innerHTML;
    });

    this.setAttribute('data-count', newDrawer.dataset.count);
    this.dataset.requiredDiscounts = newDrawer.dataset.requiredDiscounts || '';
    this.dataset.managedDiscounts = newDrawer.dataset.managedDiscounts || '';
    this.dataset.appliedDiscounts = newDrawer.dataset.appliedDiscounts || '';

    const progress = this.querySelector('[data-progress="shipping"]');
    const newProgress = newDrawer.querySelector('[data-progress="shipping"]');
    if (progress && newProgress) {
      progress.style.width = newProgress.style.width;

      const label = this.querySelector('[data-shipping-label]');
      const newLabel = newDrawer.querySelector('[data-shipping-label]');
      if (label && newLabel) label.innerHTML = newLabel.innerHTML;
    }

    document.querySelectorAll('[cart-count]').forEach((el) => {
      el.textContent = newDrawer.dataset.count;
    });

    this.syncGift();
    this.syncDiscounts();
  }

  /**
   * Pack-size discounts. Every quantity_option metaobject can carry a discount
   * code; Liquid ships the code each line currently qualifies for
   * ([data-required-discounts]), every code the theme owns
   * ([data-managed-discounts]) and what the cart holds now
   * ([data-applied-discounts]). Codes a shopper entered themselves are never
   * dropped — only managed ones are reconciled.
   */
  parseDiscountCodes(value) {
    return (value || '')
      .split(',')
      .map((code) => code.trim())
      .filter(Boolean);
  }

  parseDiscountMap(value) {
    const map = {};

    (value || '')
      .split('||')
      .map((pair) => pair.trim())
      .filter(Boolean)
      .forEach((pair) => {
        const separator = pair.indexOf(':');
        if (separator === -1) return;

        const quantity = pair.slice(0, separator).trim();
        const code = pair.slice(separator + 1).trim();
        if (quantity && code) map[quantity] = code;
      });

    return map;
  }

  /** The best code a line qualifies for: the largest pack size it reaches. */
  resolveDiscountCode(discountMap, quantity) {
    let bestQuantity = 0;
    let bestCode = '';

    Object.keys(discountMap).forEach((key) => {
      const optionQuantity = parseInt(key, 10);
      if (optionQuantity <= quantity && optionQuantity > bestQuantity) {
        bestQuantity = optionQuantity;
        bestCode = discountMap[key];
      }
    });

    return bestCode;
  }

  computeTargetDiscounts(quantityOverrides = {}, extraCodes = []) {
    const managed = this.parseDiscountCodes(this.dataset.managedDiscounts);
    const applied = this.parseDiscountCodes(this.dataset.appliedDiscounts);
    const manual = applied.filter((code) => !managed.includes(code));

    const required = [];
    this.querySelectorAll('[data-item]').forEach((itemEl) => {
      const line = parseInt(itemEl.dataset.item, 10);
      const quantity = Object.prototype.hasOwnProperty.call(quantityOverrides, line)
        ? quantityOverrides[line]
        : parseInt(itemEl.querySelector('[name="quantity"]')?.value ?? itemEl.dataset.quantity, 10);

      const code = this.resolveDiscountCode(this.parseDiscountMap(itemEl.dataset.discountMap), quantity);
      if (code) required.push(code);
    });

    return [...new Set([...manual, ...required, ...extraCodes.filter(Boolean)])];
  }

  applyDiscounts(codes) {
    return fetch(`${window.Shopify.routes.root}cart/update.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        discount: codes.join(','),
        sections: ['cart-drawer'],
        sections_url: window.location.pathname
      })
    })
      .then((response) => response.json())
      .then((response) => {
        if (response.sections) this.renderContent(response.sections);
        return response;
      });
  }

  syncDiscounts() {
    const required = this.parseDiscountCodes(this.dataset.requiredDiscounts);
    const managed = this.parseDiscountCodes(this.dataset.managedDiscounts);
    const applied = this.parseDiscountCodes(this.dataset.appliedDiscounts);

    const manual = applied.filter((code) => !managed.includes(code));
    const target = [...new Set([...manual, ...required])];

    const isSynced = target.length === applied.length && target.every((code) => applied.includes(code));
    if (isSynced) {
      this.discountAttempts = 0;
      return;
    }

    // Shopify silently drops codes it rejects, so stop instead of looping on them.
    if (this.discountAttempts >= 2) return;
    this.discountAttempts = (this.discountAttempts || 0) + 1;

    this.applyDiscounts(target).catch((error) => console.error(error));
  }

  /**
   * Free gift. Liquid ships the current state in [data-gift-state]; the drawer
   * adds the gift line once the cart total reaches the goal and drops it when
   * the total falls back under. A gift the shopper removed themselves stays out
   * until the goal is crossed again.
   */
  syncGift() {
    if (this.giftSyncing) return;

    const state = this.querySelector('[data-gift-state]');
    if (!state || !state.dataset.giftVariant) return;

    const threshold = parseInt(state.dataset.giftThreshold, 10);
    const total = parseInt(state.dataset.cartTotal, 10);
    const line = parseInt(state.dataset.giftLine, 10);

    if (total < threshold) {
      this.setGiftDismissed(false);
      if (line) this.runGiftSync(this.updateQuantity(line, 0));
      return;
    }

    if (!line && !this.isGiftDismissed()) {
      this.runGiftSync(this.addItemToCart(state.dataset.giftVariant));
    }
  }

  runGiftSync(request) {
    this.giftSyncing = true;
    request.finally(() => {
      this.giftSyncing = false;
    });
  }

  isGiftDismissed() {
    try {
      return sessionStorage.getItem('cart-gift-dismissed') === '1';
    } catch (error) {
      return this.giftDismissed === true;
    }
  }

  setGiftDismissed(state) {
    this.giftDismissed = state;

    try {
      if (state) {
        sessionStorage.setItem('cart-gift-dismissed', '1');
      } else {
        sessionStorage.removeItem('cart-gift-dismissed');
      }
    } catch (error) {
      console.warn('Unable to store the gift state', error);
    }
  }

  showError(error, line) {
    console.error(error);

    const messageBlock = this.querySelector(`[data-item="${line}"] [item-notification]`);
    if (!messageBlock) return;

    messageBlock.innerText = error.message;
    setTimeout(() => {
      messageBlock.innerText = '';
    }, 5000);
  }

  toggleLoading(state, itemEl) {
    this.isLoading = state ?? !this.isLoading;
    this.classList.toggle('loading', this.isLoading);
    if (itemEl) itemEl.classList.toggle('loading', this.isLoading);
  }

  open() {
    if (this.classList.contains('open')) return;
    this.classList.add('open');
    togglePageScroll(true);
  }

  close() {
    if (!this.classList.contains('open')) return;
    this.classList.remove('open');
    togglePageScroll(false);
  }
}

if (document.querySelector('cart-drawer')) {
  customElements.define('cart-drawer', CartDrawer);
}

/**
 * <back-in-stock> — global "notify me" popup (snippets/back-in-stock.liquid).
 * Any trigger with data-action="open-back-in-stock" opens it; the variant comes
 * from the trigger, or from the live selection inside <main-product>. Submitting
 * posts a Klaviyo back-in-stock subscription and, while the consent box is on,
 * a newsletter list subscription.
 */
class BackInStock extends HTMLElement {
  connectedCallback() {
    this.form = this.querySelector('[data-bis-form]');
    this.errorBlock = this.querySelector('[data-bis-error]');
    this.emailInput = this.querySelector('[name="email"]');

    this.addEventListener('click', (event) => {
      if (event.target === this || event.target.closest('[data-action="close"]')) this.close();
    });

    this.form?.addEventListener('submit', (event) => {
      event.preventDefault();
      this.submit();
    });

    document.addEventListener('click', (event) => {
      const trigger = event.target.closest('[data-action="open-back-in-stock"]');
      if (!trigger) return;

      event.preventDefault();
      this.open(trigger);
    });

    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && this.classList.contains('open')) this.close();
    });
  }

  open(trigger) {
    const mainProduct = trigger.closest('main-product');
    this.variantId = mainProduct?.querySelector('[name="id"]')?.value || trigger.dataset.variant;

    this.classList.remove('success', 'loading');
    this.form?.reset();
    if (this.errorBlock) this.errorBlock.textContent = '';

    this.classList.add('open');
    togglePageScroll(true);

    setTimeout(() => this.emailInput?.focus(), 100);
  }

  close() {
    if (!this.classList.contains('open')) return;

    this.classList.remove('open');
    togglePageScroll(false);
  }

  submit() {
    if (this.classList.contains('loading')) return;
    if (!reportInputValidity(this.emailInput, this.errorBlock)) return;

    const companyId = this.dataset.companyId;
    if (!companyId || !this.variantId) {
      this.showError();
      return;
    }

    this.classList.add('loading');

    const headers = {
      revision: '2024-02-15',
      'Content-Type': 'application/json',
      'X-Klaviyo-Onsite': '1'
    };

    const email = this.emailInput.value.trim();
    const requests = [
      fetch(`https://a.klaviyo.com/client/back-in-stock-subscriptions/?company_id=${companyId}`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          data: {
            type: 'back-in-stock-subscription',
            attributes: {
              profile: { data: { type: 'profile', attributes: { email } } },
              channels: ['EMAIL']
            },
            relationships: {
              variant: { data: { type: 'catalog-variant', id: `$shopify:::$default:::${this.variantId}` } }
            }
          }
        })
      })
    ];

    const listId = this.dataset.listId;
    if (this.querySelector('[name="consent"]')?.checked && listId) {
      requests.push(
        fetch(`https://a.klaviyo.com/client/subscriptions/?company_id=${companyId}`, {
          method: 'POST',
          headers,
          body: JSON.stringify({
            data: {
              type: 'subscription',
              attributes: {
                profile: {
                  data: {
                    type: 'profile',
                    attributes: {
                      email,
                      subscriptions: { email: { marketing: { consent: 'SUBSCRIBED' } } }
                    }
                  }
                }
              },
              relationships: { list: { data: { type: 'list', id: listId } } }
            }
          })
        })
      );
    }

    Promise.all(requests)
      .then((responses) => {
        if (!responses.every((response) => response.ok)) throw new Error('Klaviyo request failed');
        this.classList.add('success');
      })
      .catch((error) => {
        console.error(error);
        this.showError();
      })
      .finally(() => this.classList.remove('loading'));
  }

  showError() {
    if (this.errorBlock) this.errorBlock.textContent = window.errorStrings?.request ?? '';
  }
}

if (document.querySelector('back-in-stock')) {
  customElements.define('back-in-stock', BackInStock);
}

/**
 * <localization-form> — country/language switcher. Wraps the Shopify
 * localization form and submits it on selection.
 */
class LocalizationForm extends HTMLElement {
  connectedCallback() {
    this.form = this.querySelector('form');
    this.addEventListener('click', this.onClick.bind(this));

    document.addEventListener('click', (event) => {
      if (!this.contains(event.target)) this.classList.remove('open');
    });

    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') this.classList.remove('open');
    });
  }

  onClick(event) {
    const trigger = event.target.closest('[data-action]');
    if (!trigger || !this.contains(trigger)) return;

    event.preventDefault();

    switch (trigger.dataset.action) {
      case 'toggle-options':
        this.classList.toggle('open');
        break;

      case 'select-option': {
        const input = this.form?.querySelector(`[name="${trigger.dataset.field}"]`);
        if (!input) break;

        input.value = trigger.dataset.value;
        this.classList.remove('open');
        this.form.submit();
        break;
      }
    }
  }
}

if (document.querySelector('localization-form')) {
  customElements.define('localization-form', LocalizationForm);
}

/**
 * <contact-form> — native Shopify contact form (sections/contact-form.liquid).
 * Submits with a page reload; server re-renders the section with the success
 * message and Shopify's #<form id> anchor scrolls the user back to it. This
 * component only adds custom-styled <select> dropdowns and client-side
 * validation (required fields, email/phone format, consent checkbox) — invalid
 * fields get `.contact-form__field--error` and their `[data-field-error]` shown.
 */
class ContactForm extends HTMLElement {
  connectedCallback() {
    this.form = this.querySelector('form');

    this.onClick = this.onClick.bind(this);
    this.onKeydown = this.onKeydown.bind(this);
    this.onDocumentClick = this.onDocumentClick.bind(this);
    this.onSubmit = this.onSubmit.bind(this);

    this.addEventListener('click', this.onClick);
    this.addEventListener('keydown', this.onKeydown);
    this.addEventListener('input', (event) => this.clearError(event.target));
    this.addEventListener('change', (event) => this.clearError(event.target));
    this.form?.addEventListener('submit', this.onSubmit);

    this.querySelectorAll('[data-phone-prefix]').forEach((input) => this.initPhone(input));

    document.addEventListener('click', this.onDocumentClick);
    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') this.closeSelects();
    });

    const success = this.querySelector('.contact-form__success');
    if (success) {
      const hash = decodeURIComponent(location.hash);
      const id = this.form?.id;
      const alone = document.querySelectorAll('contact-form').length === 1;
      const isTarget = alone || hash === '' || (id && `#${id}` === hash);

      if (isTarget) this.scrollIntoView({ behavior: 'smooth', block: 'start' });
      else success.remove();
    }
  }

  onDocumentClick(event) {
    if (!this.contains(event.target)) this.closeSelects();
  }

  initPhone(input) {
    const prefix = input.dataset.phonePrefix;
    const prefixDigits = prefix.replace(/\D/g, '');
    const maxDigits = Number(input.dataset.phoneDigits) || 0;

    const normalize = () => {
      let digits = input.value.replace(/\D/g, '');
      if (prefixDigits && digits.startsWith(prefixDigits)) digits = digits.slice(prefixDigits.length);
      if (maxDigits) digits = digits.slice(0, maxDigits);
      input.value = prefix + digits;
    };

    const toEnd = () => {
      if (input.selectionStart < prefix.length) input.setSelectionRange(input.value.length, input.value.length);
    };

    input.addEventListener('focus', () => {
      if (input.value.trim() === '') input.value = prefix;
      requestAnimationFrame(toEnd);
    });

    input.addEventListener('click', toEnd);

    input.addEventListener('keydown', (event) => {
      const caret = input.selectionStart;
      const guarded =
        (event.key === 'Backspace' && caret <= prefix.length && input.selectionEnd === caret) ||
        (event.key === 'Delete' && caret < prefix.length);
      if (guarded) event.preventDefault();
    });

    input.addEventListener('input', () => {
      const atEnd = input.selectionStart === input.value.length;
      normalize();
      if (atEnd) input.setSelectionRange(input.value.length, input.value.length);
    });

    input.addEventListener('blur', () => {
      if (input.value === prefix) input.value = '';
    });
  }

  onClick(event) {
    const submit = event.target.closest('[type="submit"]');
    if (submit && this.contains(submit) && !this.validate()) {
      event.preventDefault();
      event.stopPropagation();
      return;
    }

    const trigger = event.target.closest('[data-action]');
    if (!trigger || !this.contains(trigger)) {
      this.closeSelects(event.target.closest('[data-select]'));
      return;
    }

    this.closeSelects(trigger.closest('[data-select]'));

    switch (trigger.dataset.action) {
      case 'toggle-options': {
        event.preventDefault();
        const select = trigger.closest('[data-select]');
        const willOpen = !select.classList.contains('open');
        this.closeSelects(willOpen ? select : null);
        if (willOpen) this.openSelect(select);
        else this.closeSelect(select);
        break;
      }

      case 'select-option': {
        event.preventDefault();
        const select = trigger.closest('[data-select]');
        const input = select.querySelector('[data-select-input]');
        const valueEl = select.querySelector('[data-select-value]');

        input.value = trigger.dataset.value;
        if (valueEl) valueEl.textContent = trigger.textContent.trim();
        select.classList.add('is-filled');
        select
          .querySelectorAll('.contact-form__option')
          .forEach((option) => option.classList.remove('is-selected', 'is-active'));
        trigger.classList.add('is-selected');

        this.closeSelect(select);
        this.clearError(input);
        break;
      }
    }
  }

  onKeydown(event) {
    const select = event.target.closest('[data-select]');
    if (!select || !select.classList.contains('open')) return;

    const options = [...select.querySelectorAll('.contact-form__option')];
    let index = options.indexOf(select.querySelector('.contact-form__option.is-active'));

    if (event.key === 'ArrowDown') {
      index = Math.min(options.length - 1, index + 1);
    } else if (event.key === 'ArrowUp') {
      index = Math.max(0, index - 1);
    } else if (event.key === 'Enter') {
      event.preventDefault();
      options[index]?.click();
      return;
    } else {
      return;
    }

    event.preventDefault();
    options.forEach((option) => option.classList.remove('is-active'));
    options[index]?.classList.add('is-active');
    options[index]?.scrollIntoView({ block: 'nearest' });
  }

  openSelect(select) {
    select.classList.add('open');
    select.querySelector('[data-options]').hidden = false;
    select.querySelector('[data-action="toggle-options"]')?.setAttribute('aria-expanded', 'true');

    const active =
      select.querySelector('.contact-form__option.is-selected') ||
      select.querySelector('.contact-form__option');
    active?.classList.add('is-active');
    active?.scrollIntoView({ block: 'nearest' });
  }

  closeSelect(select) {
    select.classList.remove('open');
    select.querySelector('[data-options]').hidden = true;
    select.querySelector('[data-action="toggle-options"]')?.setAttribute('aria-expanded', 'false');
    select
      .querySelectorAll('.contact-form__option.is-active')
      .forEach((option) => option.classList.remove('is-active'));
  }

  closeSelects(except) {
    this.querySelectorAll('[data-select].open').forEach((select) => {
      if (select !== except) this.closeSelect(select);
    });
  }

  clearError(target) {
    const field = target?.closest?.('[data-field], [data-consent]');
    if (!field) return;

    field.classList.remove('contact-form__field--error');
    const errorBlock = field.querySelector('[data-field-error], [data-consent-error]');
    if (errorBlock) errorBlock.hidden = true;
  }

  validate() {
    this.closeSelects();
    let firstInvalid = null;

    this.querySelectorAll('[data-field]').forEach((field) => {
      const isSelect = Boolean(field.querySelector('[data-select]'));
      const control = isSelect
        ? field.querySelector('[data-select-input]')
        : field.querySelector('input, textarea');
      if (!control) return;

      const value = control.value.trim();
      const required = control.hasAttribute('data-required');
      let isValid = true;

      if (required && value === '') {
        isValid = false;
      } else if (value !== '' && control.type === 'email' && !/^[^@\s]+@[^@\s.]+\.[^@\s]+$/.test(value)) {
        isValid = false;
      } else if (value !== '' && control.type === 'tel' && !/^[+()\d][\d\s()-]{5,}$/.test(value)) {
        isValid = false;
      }

      field.classList.toggle('contact-form__field--error', !isValid);
      const errorBlock = field.querySelector('[data-field-error]');
      if (errorBlock) errorBlock.hidden = isValid;
      if (!isValid && !firstInvalid) firstInvalid = field;
    });

    const consent = this.querySelector('[data-consent]');
    if (consent) {
      const box = consent.querySelector('[data-consent-input]');
      const isValid = Boolean(box && box.checked);

      consent.classList.toggle('contact-form__field--error', !isValid);
      const errorBlock = consent.querySelector('[data-consent-error]');
      if (errorBlock) errorBlock.hidden = isValid;
      if (!isValid && !firstInvalid) firstInvalid = consent;
    }

    if (firstInvalid) {
      firstInvalid.scrollIntoView({ behavior: 'smooth', block: 'center' });
      firstInvalid.querySelector('input, textarea, button')?.focus({ preventScroll: true });
    }

    return !firstInvalid;
  }

  onSubmit(event) {
    if (!this.validate()) {
      event.preventDefault();
      event.stopPropagation();
    }
  }
}

if (document.querySelector('contact-form')) {
  customElements.define('contact-form', ContactForm);
}
